import {type ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {alpha} from "@mui/material/styles";
import {type GridColDef} from "@mui/x-data-grid";
import {useTranslation} from "react-i18next";
import {
    useAdminLegalEntities,
    useAdminLegalEntityLlmConfigs,
    useAdminLlmModels,
    useDeleteLegalEntityLlmConfig,
    useSyncAdminLlmModels,
    useUpdateDefaultLlmModel,
    useUpsertLegalEntityLlmConfig,
} from "../../api/admin/adminQueries";
import AdminDataGrid from "./AdminDataGrid";
import SectionShell from "./AdminShared";
import {type LegalEntity, type LegalEntityLlmConfig, type LlmModel} from "./AdminTypes";
import ConfirmDialog from "../dialog/ConfirmDialog";

type OverrideDraft = {
    legalEntityId: string;
    llmModelId: string;
};

const createInitialOverrideDraft = (): OverrideDraft => ({
    legalEntityId: "",
    llmModelId: "",
});

const emptyLegalEntities: LegalEntity[] = [];
const emptyLlmModels: LlmModel[] = [];
const emptyOverrides: LegalEntityLlmConfig[] = [];
const compactSelectSx = {
    width: {xs: "100%", md: 280},
    flexShrink: 0,
};
const legalEntitySelectSx = {
    width: {xs: "100%", md: 340},
    flexShrink: 0,
};

const SettingsPanel = ({
    eyebrow,
    title,
    description,
    children,
}: {
    eyebrow: string;
    title: string;
    description: string;
    children: ReactNode;
}) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            p: {xs: 2.25, md: 2.5},
            borderRadius: 3,
            border: "1px solid",
            borderColor: theme.palette.divider,
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.86 : 0.94),
        })}
    >
        <Stack spacing={2.25}>
            <Box>
                <Typography
                    sx={(theme) => ({
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        color: theme.palette.text.secondary,
                        mb: 0.5,
                    })}
                >
                    {eyebrow}
                </Typography>
                <Typography
                    sx={(theme) => ({
                        fontFamily: theme.typography.h4.fontFamily || theme.typography.fontFamily,
                        fontWeight: 800,
                        fontSize: 21,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                    })}
                >
                    {title}
                </Typography>
                <Typography color="text.secondary">{description}</Typography>
            </Box>
            {children}
        </Stack>
    </Paper>
);

const LlmConfigTab = () => {
    const {t: translate} = useTranslation();
    const [defaultModelIdDraft, setDefaultModelIdDraft] = useState("");
    const [overrideDraft, setOverrideDraft] = useState<OverrideDraft>(createInitialOverrideDraft);
    const [editingOverride, setEditingOverride] = useState<LegalEntityLlmConfig | null>(null);
    const [editingModelIdDraft, setEditingModelIdDraft] = useState("");
    const [overridePendingDeletion, setOverridePendingDeletion] = useState<LegalEntityLlmConfig | null>(null);
    const legalEntitiesQuery = useAdminLegalEntities();
    const llmModelsQuery = useAdminLlmModels();
    const overridesQuery = useAdminLegalEntityLlmConfigs();
    const syncLlmModelsMutation = useSyncAdminLlmModels();
    const updateDefaultMutation = useUpdateDefaultLlmModel();
    const upsertOverrideMutation = useUpsertLegalEntityLlmConfig();
    const deleteOverrideMutation = useDeleteLegalEntityLlmConfig();
    const legalEntities = legalEntitiesQuery.data ?? emptyLegalEntities;
    const llmModels = llmModelsQuery.data ?? emptyLlmModels;
    const overrides = useMemo(
        () =>
            [...(overridesQuery.data ?? emptyOverrides)].sort((left, right) =>
                left.legalEntityName.localeCompare(right.legalEntityName),
            ),
        [overridesQuery.data],
    );
    const defaultModel = useMemo(() => llmModels.find((model) => model.isDefault) ?? null, [llmModels]);
    const overriddenLegalEntityIds = new Set(overrides.map((override) => override.legalEntityId));
    const availableLegalEntities = legalEntities.filter((legalEntity) => !overriddenLegalEntityIds.has(legalEntity.id));
    const sharedConfigErrorMessage =
        syncLlmModelsMutation.error?.message || legalEntitiesQuery.error?.message || llmModelsQuery.error?.message;
    const isSyncingModels = syncLlmModelsMutation.isPending;
    const isCreateOverrideUsingDefaultModel =
        Boolean(defaultModel?.id) && overrideDraft.llmModelId === defaultModel?.id;
    const isEditOverrideUsingDefaultModel = Boolean(defaultModel?.id) && editingModelIdDraft === defaultModel?.id;

    useEffect(() => {
        setDefaultModelIdDraft(defaultModel?.id ?? "");
    }, [defaultModel?.id]);

    useEffect(() => {
        if (!overrideDraft.legalEntityId && availableLegalEntities.length > 0) {
            setOverrideDraft((previousDraft) => ({...previousDraft, legalEntityId: availableLegalEntities[0].id}));
        }
    }, [availableLegalEntities, overrideDraft.legalEntityId]);

    useEffect(() => {
        if (!overrideDraft.llmModelId && llmModels.length > 0) {
            setOverrideDraft((previousDraft) => ({...previousDraft, llmModelId: llmModels[0].id}));
        }
    }, [llmModels, overrideDraft.llmModelId]);

    useEffect(() => {
        if (editingOverride) {
            setEditingModelIdDraft(editingOverride.llmModelId);
        }
    }, [editingOverride]);

    const handleUpdateDefaultModel = async () => {
        if (!defaultModelIdDraft || defaultModelIdDraft === defaultModel?.id) {
            return;
        }

        await updateDefaultMutation.mutateAsync({llmModelId: defaultModelIdDraft});
    };

    const handleSyncLlmModels = async() => {
        await syncLlmModelsMutation.mutateAsync();
    };

    const handleCreateOverride = async () => {
        if (!overrideDraft.legalEntityId || !overrideDraft.llmModelId) {
            return;
        }

        await upsertOverrideMutation.mutateAsync(overrideDraft);
        setOverrideDraft(createInitialOverrideDraft());
    };

    const handleOpenEditOverride = useCallback(
        (override: LegalEntityLlmConfig) => {
            upsertOverrideMutation.reset();
            setEditingOverride(override);
        },
        [upsertOverrideMutation],
    );

    const handleCloseEditOverride = useCallback(() => {
        upsertOverrideMutation.reset();
        setEditingOverride(null);
    }, [upsertOverrideMutation]);

    const handleUpdateOverride = async () => {
        if (!editingOverride || !editingModelIdDraft) {
            return;
        }

        await upsertOverrideMutation.mutateAsync({
            legalEntityId: editingOverride.legalEntityId,
            llmModelId: editingModelIdDraft,
        });
        setEditingOverride(null);
    };

    const handleOpenDeleteOverride = useCallback(
        (override: LegalEntityLlmConfig) => {
            deleteOverrideMutation.reset();
            setOverridePendingDeletion(override);
        },
        [deleteOverrideMutation],
    );

    const handleCloseDeleteOverride = useCallback(() => {
        deleteOverrideMutation.reset();
        setOverridePendingDeletion(null);
    }, [deleteOverrideMutation]);

    const handleDeleteOverride = async () => {
        if (!overridePendingDeletion) {
            return;
        }

        await deleteOverrideMutation.mutateAsync({legalEntityId: overridePendingDeletion.legalEntityId});
        setOverridePendingDeletion(null);
    };

    const isBusy =
        updateDefaultMutation.isPending || upsertOverrideMutation.isPending || deleteOverrideMutation.isPending;

    const overrideColumns = useMemo<GridColDef<LegalEntityLlmConfig>[]>(
        () => [
            {
                field: "legalEntityName",
                headerName: translate("admin.shared.legalEntity"),
                flex: 1.15,
                minWidth: 220,
                sortable: false,
                renderCell: ({row}) => <Typography sx={{fontWeight: 700}}>{row.legalEntityName}</Typography>,
            },
            {
                field: "llmModelName",
                headerName: translate("admin.llmConfig.columns.overrideModel"),
                flex: 0.85,
                minWidth: 180,
                sortable: false,
                renderCell: ({row}) => <Chip label={row.llmModelName} size="small" sx={{fontWeight: 700}} />,
            },
            {
                field: "actions",
                headerName: translate("admin.shared.actions"),
                flex: 0.75,
                minWidth: 140,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: "right",
                headerAlign: "right",
                renderCell: ({row}) => (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{width: "100%"}}>
                        <Tooltip title={translate("admin.llmConfig.tooltips.adjustOverride")}>
                            <IconButton size="small" onClick={() => handleOpenEditOverride(row)}>
                                <EditRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={translate("admin.llmConfig.tooltips.removeOverride")}>
                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteOverride(row)}>
                                <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleOpenDeleteOverride, handleOpenEditOverride, translate],
    );

    return (
        <Stack spacing={3}>
            <SectionShell eyebrow={translate("admin.llmConfig.eyebrow")} title={translate("admin.llmConfig.title")}>
                <Stack spacing={3}>
                    <SettingsPanel
                        eyebrow={translate("admin.llmConfig.defaultSection.eyebrow")}
                        title={translate("admin.llmConfig.defaultSection.title")}
                        description={translate("admin.llmConfig.defaultSection.description")}
                    >
                        {updateDefaultMutation.error?.message || sharedConfigErrorMessage ? (
                            <Alert severity="error">
                                {updateDefaultMutation.error?.message || sharedConfigErrorMessage}
                            </Alert>
                        ) : null}
                        <Stack
                            direction={{xs: "column", md: "row"}}
                            spacing={1.5}
                            alignItems={{xs: "stretch", md: "center"}}
                        >
                            <Box sx={{position: "relative", ...compactSelectSx}}>
                                <FormControl
                                    fullWidth
                                    disabled={llmModelsQuery.isLoading || isBusy || isSyncingModels}
                                >
                                    <InputLabel id="default-llm-model-label">
                                        {translate("admin.llmConfig.defaultSection.defaultLlmModel")}
                                    </InputLabel>
                                    <Select
                                        labelId="default-llm-model-label"
                                        label={translate("admin.llmConfig.defaultSection.defaultLlmModel")}
                                        value={defaultModelIdDraft}
                                        onChange={(event) => setDefaultModelIdDraft(event.target.value)}
                                    >
                                        {llmModels.map((model) => (
                                            <MenuItem key={model.id} value={model.id}>
                                                {model.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {isSyncingModels ? (
                                    <Box
                                        sx={(theme) => ({
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: 1,
                                            bgcolor: alpha(theme.palette.background.paper, 0.65),
                                            zIndex: 1,
                                        })}
                                    >
                                        <CircularProgress size={22}/>
                                    </Box>
                                ) : null}
                            </Box>
                            <Tooltip title={translate("common.refresh")}>
                                <span>
                                    <IconButton
                                        onClick={handleSyncLlmModels}
                                        disabled={isBusy || isSyncingModels}
                                        sx={{
                                            alignSelf: {xs: "stretch", md: "center"},
                                            border: "1px solid",
                                            borderColor: "primary.main",
                                            borderRadius: 2,
                                            width: 48,
                                            height: 48,
                                        }}
                                    >
                                        <RefreshRoundedIcon/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Button
                                variant="contained"
                                startIcon={<SaveRoundedIcon />}
                                onClick={handleUpdateDefaultModel}
                                disabled={
                                    isBusy ||
                                    isSyncingModels ||
                                    !defaultModelIdDraft ||
                                    defaultModelIdDraft === defaultModel?.id
                                }
                                sx={{
                                    fontWeight: 700,
                                    boxShadow: "none",
                                    minWidth: 160,
                                    alignSelf: {xs: "stretch", md: "center"},
                                    height: 48,
                                }}
                            >
                                {translate("admin.llmConfig.defaultSection.saveDefault")}
                            </Button>
                        </Stack>
                    </SettingsPanel>

                    <SettingsPanel
                        eyebrow={translate("admin.llmConfig.overridesSection.eyebrow")}
                        title={translate("admin.llmConfig.overridesSection.title")}
                        description={translate("admin.llmConfig.overridesSection.description")}
                    >
                        {!editingOverride && (upsertOverrideMutation.error?.message || sharedConfigErrorMessage) ? (
                            <Alert severity="error">
                                {upsertOverrideMutation.error?.message || sharedConfigErrorMessage}
                            </Alert>
                        ) : null}
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", md: "340px 280px auto"},
                                gap: 1.5,
                                alignItems: "center",
                                justifyContent: "flex-start",
                            }}
                        >
                            <FormControl
                                fullWidth
                                disabled={availableLegalEntities.length === 0 || legalEntitiesQuery.isLoading || isBusy}
                                sx={legalEntitySelectSx}
                            >
                                <InputLabel id="override-legal-entity-label">
                                    {translate("admin.shared.legalEntity")}
                                </InputLabel>
                                <Select
                                    labelId="override-legal-entity-label"
                                    label={translate("admin.shared.legalEntity")}
                                    value={overrideDraft.legalEntityId}
                                    onChange={(event) =>
                                        setOverrideDraft((previousDraft) => ({
                                            ...previousDraft,
                                            legalEntityId: event.target.value,
                                        }))
                                    }
                                >
                                    {availableLegalEntities.map((legalEntity) => (
                                        <MenuItem key={legalEntity.id} value={legalEntity.id}>
                                            {legalEntity.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                fullWidth
                                disabled={llmModelsQuery.isLoading || isBusy || isSyncingModels}
                                sx={compactSelectSx}
                            >
                                <InputLabel id="override-llm-model-label">
                                    {translate("admin.shared.llmModel")}
                                </InputLabel>
                                <Select
                                    labelId="override-llm-model-label"
                                    label={translate("admin.shared.llmModel")}
                                    value={overrideDraft.llmModelId}
                                    onChange={(event) =>
                                        setOverrideDraft((previousDraft) => ({
                                            ...previousDraft,
                                            llmModelId: event.target.value,
                                        }))
                                    }
                                >
                                    {llmModels.map((model) => (
                                        <MenuItem key={model.id} value={model.id}>
                                            {model.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                startIcon={<PlaylistAddRoundedIcon />}
                                onClick={handleCreateOverride}
                                disabled={
                                    isBusy ||
                                    availableLegalEntities.length === 0 ||
                                    !overrideDraft.legalEntityId ||
                                    !overrideDraft.llmModelId ||
                                    isCreateOverrideUsingDefaultModel ||
                                    isSyncingModels
                                }
                                sx={{
                                    fontWeight: 700,
                                    boxShadow: "none",
                                    minWidth: 150,
                                    height: 56,
                                    alignSelf: {xs: "stretch", md: "center"},
                                }}
                            >
                                {translate("admin.llmConfig.overridesSection.addOverride")}
                            </Button>
                        </Box>

                        {isCreateOverrideUsingDefaultModel ? (
                            <Alert severity="info">
                                {translate("admin.llmConfig.overridesSection.selectDifferentModel")}
                            </Alert>
                        ) : null}

                        {availableLegalEntities.length === 0 ? (
                            <Alert severity="info">
                                {translate("admin.llmConfig.overridesSection.allLegalEntitiesConfigured")}
                            </Alert>
                        ) : null}
                    </SettingsPanel>
                </Stack>
            </SectionShell>

            <SectionShell
                eyebrow={translate("admin.llmConfig.currentOverrides.eyebrow")}
                title={translate("admin.llmConfig.currentOverrides.title")}
            >
                <Stack spacing={2}>
                    {overridesQuery.error?.message ? (
                        <Alert severity="error">{overridesQuery.error.message}</Alert>
                    ) : null}
                    <Typography color="text.secondary">
                        {translate("admin.llmConfig.currentOverrides.description")}
                    </Typography>

                    {!overridesQuery.isLoading && overrides.length === 0 ? (
                        <Alert severity="info">{translate("admin.llmConfig.currentOverrides.empty")}</Alert>
                    ) : null}

                    <AdminDataGrid
                        rows={overrides}
                        columns={overrideColumns}
                        getRowId={(row) => row.legalEntityId}
                        autoHeight
                        loading={overridesQuery.isLoading || overridesQuery.isFetching}
                    />
                </Stack>
            </SectionShell>

            <Dialog
                open={Boolean(editingOverride)}
                onClose={isBusy ? undefined : handleCloseEditOverride}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{translate("admin.llmConfig.editDialog.title")}</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        {editingOverride && upsertOverrideMutation.error?.message ? (
                            <Alert severity="error">{upsertOverrideMutation.error.message}</Alert>
                        ) : null}
                        <TextField
                            label={translate("admin.shared.legalEntity")}
                            value={editingOverride?.legalEntityName ?? ""}
                            InputProps={{readOnly: true}}
                            fullWidth
                        />
                        <FormControl
                            fullWidth
                            disabled={llmModelsQuery.isLoading || isBusy || isSyncingModels}
                            sx={{width: {xs: "100%", sm: 280}}}
                        >
                            <InputLabel id="edit-override-llm-model-label">
                                {translate("admin.shared.llmModel")}
                            </InputLabel>
                            <Select
                                labelId="edit-override-llm-model-label"
                                label={translate("admin.shared.llmModel")}
                                value={editingModelIdDraft}
                                onChange={(event) => setEditingModelIdDraft(event.target.value)}
                            >
                                {llmModels.map((model) => (
                                    <MenuItem key={model.id} value={model.id}>
                                        {model.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {isEditOverrideUsingDefaultModel ? (
                            <Alert severity="info">{translate("admin.llmConfig.editDialog.matchesDefault")}</Alert>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button variant="outlined" onClick={handleCloseEditOverride} disabled={isBusy}>
                        {translate("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateOverride}
                        disabled={
                            isBusy ||
                            isSyncingModels ||
                            !editingModelIdDraft ||
                            editingModelIdDraft === editingOverride?.llmModelId ||
                            isEditOverrideUsingDefaultModel
                        }
                        sx={{boxShadow: "none"}}
                    >
                        {translate("common.saveChanges")}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(overridePendingDeletion)}
                title={translate("admin.llmConfig.removeDialog.title")}
                message={translate("admin.llmConfig.removeDialog.message", {
                    legalEntityName:
                        overridePendingDeletion?.legalEntityName ??
                        translate("admin.llmConfig.removeDialog.fallbackLegalEntity"),
                })}
                confirmLabel={translate("admin.llmConfig.removeDialog.confirm")}
                cancelLabel={translate("common.cancel")}
                errorMessage={deleteOverrideMutation.error?.message}
                onConfirm={handleDeleteOverride}
                onClose={handleCloseDeleteOverride}
                isLoading={deleteOverrideMutation.isPending}
            />
        </Stack>
    );
};

export default LlmConfigTab;
